/**
 * One session talking to one other session over an open channel.
 *
 * The exchange is deliberately lopsided. The session that pressed the button drives:
 * it asks each peer what it holds, works out the union, pulls what it lacks, and only then
 * pushes. Everyone else answers questions. That asymmetry is what makes a single press
 * converge *every* session rather than only the one that started it — a peer receives not
 * just what the initiator already had, but whatever the initiator has learned from the
 * other peers in the meantime.
 *
 * The same class plays both parts, because which part a session is playing is decided by
 * who pressed the button and both roles have to be available at all times.
 *
 * ## What is checked before anything is written
 *
 * A received file is accepted only if its bytes hash to the value that was claimed for it.
 * That is not a formality. The hash is the identity the two sides reconcile on, so bytes
 * filed under the wrong one would be a model that every future share believes is present
 * and is not, and no amount of syncing would ever repair it. Checking is one pass over
 * bytes that have just been received anyway.
 */

import { hashBytes } from '../domain/hash.js';
import type {
  Amendment,
  Manifest,
  ManifestEntry,
  ModelHash,
  Tombstone,
} from '../domain/sync.js';
import { encode, parseControl } from './protocol.js';
import { type Frame, sendBlob, type Wire } from './wire.js';

/** What a conversation needs from the library it is speaking for. */
export interface Library {
  /** What this session holds, for the account doing the sharing. */
  manifest(): Promise<Manifest>;
  /** The bytes of a model, or `undefined` if it has gone since the manifest was taken. */
  bytesFor(hash: ModelHash): Promise<Blob | undefined>;
  /** Take in a model that has arrived, having already been checked. */
  accept(entry: ManifestEntry, blob: Blob): Promise<void>;
  /** Record deletions decided elsewhere. */
  forget(tombstones: readonly Tombstone[]): Promise<void>;
  /** Apply changes to how models already held are shown. */
  revise(amendments: readonly Amendment[]): Promise<void>;
}

/**
 * How long a conversation will sit in complete silence before giving up.
 *
 * A backstop, not the usual way a dead connection is noticed — a peer connection that fails
 * closes its channel, which is faster and more definite. This exists because relying on the
 * browser to notice turned out not to be safe: a session closed mid-transfer left the other
 * end waiting indefinitely, holding everything it had received so far.
 *
 * Generous on purpose. During a share with several sessions, one connection legitimately
 * sits idle while the initiator deals with another, and a large library can keep it waiting
 * for minutes. Five minutes of *nothing at all* is well past that and still bounds the wait.
 */
const SILENCE_TIMEOUT_MS = 5 * 60_000;

/** Raised when the other end says something the protocol does not allow. */
export class ProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProtocolError';
  }
}

interface Incoming {
  readonly entry: ManifestEntry;
  readonly chunks: ArrayBuffer[];
  received: number;
}

export class Conversation {
  private incoming: Incoming | undefined;

  /** Frames are handled one at a time, in order, even though handling them is async. */
  private queue: Promise<void> = Promise.resolve();

  private manifestWaiter: ((manifest: Manifest) => void) | undefined;
  private awaited: Set<ModelHash> | undefined;
  private pullWaiter: (() => void) | undefined;

  /** How a requested model should be described once it arrives. See [`pull`]. */
  private readonly planned = new Map<ModelHash, ManifestEntry>();

  private finishWaiter: (() => void) | undefined;
  /** We have said we are finished. */
  private sentDone = false;
  /** They have said they are finished, which also means they have stored what we sent. */
  private peerDone = false;

  /** Fires if the other end goes silent for long enough. See [`SILENCE_TIMEOUT_MS`]. */
  private watchdog: ReturnType<typeof setTimeout> | undefined;

  /** The first thing to go wrong. Every pending wait is failed with it. */
  private failure: Error | undefined;
  private readonly waiters = new Set<(error: Error) => void>();

  constructor(
    private readonly wire: Wire,
    private readonly library: Library,
    private readonly onProgress: (bytes: number) => void = () => undefined,
    /**
     * How long to tolerate silence. A parameter so that the behaviour can be tested in
     * milliseconds rather than minutes — the same arrangement as the backend's rate
     * limiter, whose clock is passed in for the same reason.
     */
    private readonly silenceMs: number = SILENCE_TIMEOUT_MS,
  ) {
    // Frames arrive in order and must be handled in order, but handling one is
    // asynchronous — it may write to storage. Chaining them means a second frame cannot
    // start being dealt with while the first is still half dealt with.
    wire.onFrame((frame) => {
      this.restartWatchdog();
      this.queue = this.queue
        .then(() => this.receive(frame))
        .catch((error: unknown) => {
          this.fail(error instanceof Error ? error : new ProtocolError(String(error)));
        });
    });

    wire.onClosed(() => {
      // A close after both ends have finished is the ordinary end of a conversation.
      // Before that, anything still waiting will never be answered and has to be told so
      // rather than hanging.
      if (!this.peerDone)
        this.fail(new ProtocolError('the connection closed mid-transfer'));
      this.stopWatchdog();
    });

    // Started here rather than on the first frame, because a peer that never says anything
    // at all is exactly the case this exists to catch.
    this.restartWatchdog();
  }

  // ---------------------------------------------------------------- driving

  /** Ask what the other session holds. */
  async askManifest(): Promise<Manifest> {
    this.wire.send(encode({ t: 'manifest?' }));
    return this.expect<Manifest>((resolve) => {
      this.manifestWaiter = resolve;
    });
  }

  /** Ask for models by hash, and wait until each has arrived or been disclaimed. */
  async pull(entries: readonly ManifestEntry[]): Promise<void> {
    if (entries.length === 0) return;

    const hashes = entries.map((entry) => entry.hash);
    this.awaited = new Set(hashes);
    // Kept so that what arrives is filed under the description the *merged* view settled
    // on, rather than the sending session's own. They can differ — two sessions may have
    // added the same file at different times — and `addedAt` is what a deletion is
    // weighed against, so taking the sender's would leave the sessions disagreeing about
    // which of an addition and a deletion came first.
    for (const entry of entries) this.planned.set(entry.hash, entry);

    this.wire.send(encode({ t: 'pull', hashes }));

    await this.expectNothing((resolve) => {
      this.pullWaiter = resolve;
    });
  }

  /** Send models to the other session. */
  async push(entries: readonly ManifestEntry[]): Promise<void> {
    for (const entry of entries) await this.offer(entry);
  }

  sendTombstones(tombstones: readonly Tombstone[]): void {
    if (tombstones.length === 0) return;
    this.wire.send(encode({ t: 'tombstones', tombstones }));
  }

  /**
   * Tell the other session how models it already holds should now look.
   *
   * No bytes and no acknowledgement: an amendment that does not arrive costs a colour
   * until the next share, which is not worth a round trip to be sure of.
   */
  sendAmendments(amendments: readonly Amendment[]): void {
    if (amendments.length === 0) return;
    this.wire.send(encode({ t: 'amend', amendments }));
  }

  /** Say the exchange is over. Sent once, however many times this is called. */
  finish(): void {
    if (this.sentDone) return;
    this.sentDone = true;
    this.wire.send(encode({ t: 'done' }));
  }

  /**
   * Wait for the other end to say it has finished.
   *
   * This is an acknowledgement, not a courtesy. Frames are handled strictly in order, so
   * the other end only reaches the closing message after it has stored everything sent
   * before it — and until it says so, tearing the connection down discards whatever it had
   * not finished writing. Skipping this wait loses the last file of a share, sometimes.
   */
  async untilPeerFinished(): Promise<void> {
    if (this.peerDone) return;
    await this.expectNothing((resolve) => {
      this.finishWaiter = resolve;
    });
  }

  close(): void {
    this.wire.close();
  }

  // -------------------------------------------------------------- answering

  private async receive(frame: Frame): Promise<void> {
    if (frame instanceof ArrayBuffer) {
      this.absorb(frame);
      return;
    }

    const control = parseControl(frame);
    // Unrecognised control frames are ignored rather than fatal, so a newer session
    // saying something this one has not heard of does not break the exchange.
    if (!control) return;

    switch (control.t) {
      case 'manifest?':
        this.wire.send(
          encode({ t: 'manifest', manifest: await this.library.manifest() }),
        );
        return;

      case 'manifest':
        this.manifestWaiter?.(control.manifest);
        this.manifestWaiter = undefined;
        return;

      case 'pull':
        await this.serve(control.hashes);
        return;

      case 'file':
        if (this.incoming) {
          throw new ProtocolError('a second file started before the first had finished');
        }
        this.incoming = { entry: control.entry, chunks: [], received: 0 };
        return;

      case 'end':
        await this.complete(control.hash);
        return;

      case 'missing':
        // The peer has lost it since it described its library. Nothing to be done, and
        // not an error: the next share will settle it.
        this.settle(control.hash);
        return;

      case 'tombstones':
        await this.library.forget(control.tombstones);
        return;

      case 'amend':
        await this.library.revise(control.amendments);
        return;

      case 'done':
        this.peerDone = true;
        this.stopWatchdog();
        this.finishWaiter?.();
        this.finishWaiter = undefined;
        // Reached only after every earlier frame has been dealt with, so replying is a
        // truthful "everything you sent is stored". Sending it back is what lets the
        // other end close the connection without cutting off its own last transfer.
        this.finish();
        return;
    }
  }

  /** Send each requested model, or say plainly that it is no longer here. */
  private async serve(hashes: readonly ModelHash[]): Promise<void> {
    // Described from this session's own manifest: the request names hashes only, and a
    // model needs a name and a date to be filed under at the other end.
    const held = new Map(
      (await this.library.manifest()).models.map((entry) => [entry.hash, entry] as const),
    );

    for (const hash of hashes) {
      const entry = held.get(hash);
      if (!entry) {
        this.wire.send(encode({ t: 'missing', hash }));
        continue;
      }
      await this.offer(entry);
    }
  }

  /**
   * Send one model, header first.
   *
   * The name and time come from the manifest entry rather than from anything local: a
   * copy that claimed to have been added just now would look newer than a deletion it
   * actually predates, and would quietly undo that deletion at the next share.
   */
  private async offer(entry: ManifestEntry): Promise<void> {
    const blob = await this.library.bytesFor(entry.hash);
    if (!blob) {
      this.wire.send(encode({ t: 'missing', hash: entry.hash }));
      return;
    }

    this.wire.send(encode({ t: 'file', entry: { ...entry, byteSize: blob.size } }));
    await sendBlob(this.wire, blob, this.onProgress);
    this.wire.send(encode({ t: 'end', hash: entry.hash }));
  }

  private absorb(chunk: ArrayBuffer): void {
    const incoming = this.incoming;
    if (!incoming)
      throw new ProtocolError('file bytes arrived with no file to put them in');

    incoming.received += chunk.byteLength;
    // Bounded by what the sender declared, and the declaration is itself capped when it
    // is parsed. Without this a peer could keep sending and this tab would keep buffering
    // until the browser killed it.
    if (incoming.received > incoming.entry.byteSize) {
      throw new ProtocolError(`${incoming.entry.name} sent more bytes than it declared`);
    }

    incoming.chunks.push(chunk);
    this.onProgress(chunk.byteLength);
  }

  private async complete(hash: ModelHash): Promise<void> {
    const incoming = this.incoming;
    if (incoming?.entry.hash !== hash) {
      throw new ProtocolError('a file ended that had not started');
    }
    this.incoming = undefined;

    if (incoming.received !== incoming.entry.byteSize) {
      throw new ProtocolError(`${incoming.entry.name} was truncated in transfer`);
    }

    const blob = new Blob(incoming.chunks);
    const actual = await hashBytes(await blob.arrayBuffer());
    if (actual !== hash) {
      // Filed under the wrong identity, this would be a model every future share believed
      // was present and was not, and nothing would ever repair it.
      throw new ProtocolError(`${incoming.entry.name} did not match its hash`);
    }

    // What was asked for wins over what the sender chose to call it; see [`pull`].
    await this.library.accept(this.planned.get(hash) ?? incoming.entry, blob);
    this.settle(hash);
  }

  /** Mark one awaited hash as dealt with, and finish the pull once none are left. */
  private settle(hash: ModelHash): void {
    const awaited = this.awaited;
    if (!awaited) return;

    awaited.delete(hash);
    if (awaited.size > 0) return;

    this.awaited = undefined;
    this.pullWaiter?.();
    this.pullWaiter = undefined;
  }

  // ------------------------------------------------------------------ waits

  /** The same, for the waits that carry no value. */
  private async expectNothing(begin: (resolve: () => void) => void): Promise<void> {
    await this.expect<null>((resolve) => {
      begin(() => {
        resolve(null);
      });
    });
  }

  /** A promise that also rejects if the conversation fails for any other reason. */
  private async expect<T>(begin: (resolve: (value: T) => void) => void): Promise<T> {
    if (this.failure) throw this.failure;

    return new Promise<T>((resolve, reject) => {
      this.waiters.add(reject);
      begin((value) => {
        this.waiters.delete(reject);
        resolve(value);
      });
    });
  }

  private fail(error: Error): void {
    this.failure ??= error;
    for (const reject of this.waiters) reject(this.failure);
    this.waiters.clear();
    this.stopWatchdog();
  }

  /** Start the silence clock again, having just heard something. */
  private restartWatchdog(): void {
    this.stopWatchdog();
    if (this.peerDone || this.failure) return;

    this.watchdog = setTimeout(() => {
      this.fail(new ProtocolError('the other session stopped responding'));
      // Close as well as fail: whatever is holding this connection open is not going to
      // finish with it, and a half-received model is a lot of memory to keep for nothing.
      this.wire.close();
    }, this.silenceMs);

    // A watchdog should never be the reason a process stays alive. Only meaningful under
    // Node, where the test suite runs; in a browser `setTimeout` returns a plain number.
    (this.watchdog as unknown as { unref?: () => void }).unref?.();
  }

  private stopWatchdog(): void {
    if (this.watchdog === undefined) return;
    clearTimeout(this.watchdog);
    this.watchdog = undefined;
  }
}
